import { Button, Divider, Group, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { AdPlatform } from '@marketing-tool/shared';
import { Line, Meta } from '@marketing-tool/shared';

import type { AdGroup } from '../../../data/game-report/types';
import { preventEnterSubmit } from '../../../lib/utils';

export type EditAdGroupFormData = {
    id: string | number;
    name?: string;
    configuredStatus?: string;
    dailyBudgetMicro?: number;
    // Note: Targeting and bidding settings cannot be modified after ad group creation
};

type EditAdGroupFormProps = {
    platform: AdPlatform;
    adGroup: AdGroup;
    onSubmit: (data: EditAdGroupFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
};

export const EditAdGroupForm = ({ platform, adGroup, onSubmit, onCancel, isSubmitting }: EditAdGroupFormProps) => {
    const form = useForm<EditAdGroupFormData>({
        initialValues: {
            id: adGroup.id,
            name: adGroup.name,
            configuredStatus: adGroup.configuredStatus,
            dailyBudgetMicro: adGroup.dailyBudgetMicro,
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        // Only send changed fields
        const updates: EditAdGroupFormData = { id: adGroup.id };
        if (form.isDirty('name')) updates.name = values.name;
        if (form.isDirty('configuredStatus')) updates.configuredStatus = values.configuredStatus;
        if (form.isDirty('dailyBudgetMicro')) updates.dailyBudgetMicro = values.dailyBudgetMicro;

        onSubmit(updates);
    });

    const statusOptions =
        platform === 'line'
            ? [
                  { value: Line.ConfiguredStatus.Active, label: 'Active' },
                  { value: Line.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Line.ConfiguredStatus.Draft, label: 'Draft' },
              ]
            : [
                  { value: Meta.ConfiguredStatus.Active, label: 'Active' },
                  { value: Meta.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Meta.ConfiguredStatus.Archived, label: 'Archived' },
              ];

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <TextInput
                    label="Ad Group Name"
                    placeholder="Enter ad group name"
                    required
                    disabled={isSubmitting}
                    {...form.getInputProps('name')}
                />

                <NumberInput
                    label="Daily Budget (JPY)"
                    min={0}
                    step={1}
                    value={form.values.dailyBudgetMicro ? form.values.dailyBudgetMicro / 1_000_000 : ''}
                    onChange={(val) => {
                        const value = typeof val === 'number' ? val : parseFloat(String(val));
                        form.setFieldValue('dailyBudgetMicro', isNaN(value) ? undefined : value * 1_000_000);
                    }}
                    placeholder="Enter daily budget"
                    required
                    disabled={isSubmitting}
                />

                <Select
                    label="Status"
                    required
                    data={statusOptions}
                    disabled={isSubmitting}
                    value={form.values.configuredStatus}
                    onChange={(val) => {
                        if (val !== null) form.setFieldValue('configuredStatus', val);
                    }}
                />

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
